import { Injectable } from '@angular/core';

@Injectable({
    providedIn: 'root',
})
export class ObjectService {
    /**
     * Checks if the provided value is a plain object.
     *
     * A plain object is created using `{}`, `new Object()`, or `Object.create(null)`.
     * It must have the `Object` constructor as its prototype.
     *
     * @param value The value to check.
     * @return Returns `true` if the value is a plain object, otherwise returns `false`.
     */
    public isPlainObject(value: unknown): value is Record<string, unknown> {
        return (
            typeof value === 'object' &&
            value !== null &&
            value.constructor === Object
        );
    }

    /**
     * Calculates the depth of a given object by traversing its nested structure.
     *
     * @param {unknown} obj - The object whose depth is to be determined.
     *                         Non-plain objects or non-object types will return a depth of 0.
     * @return {number} Returns the numeric depth of the object. If the object is not a plain object, returns 0.
     */
    public getObjectDepth(obj: unknown): number {
        if (!this.isPlainObject(obj)) {
            return 0;
        }

        let maxDepth = 0;
        for (const key in obj) {
            if (!Object.prototype.hasOwnProperty.call(obj, key)) {
                continue;
            }

            const value = obj[key];
            const depth = this.getObjectDepth(value);
            if (depth > maxDepth) {
                maxDepth = depth;
            }
        }

        return maxDepth + 1;
    }

    /**
     * Flattens a nested object into a single-level object with dot-separated keys.
     *
     * @param {Record<string, any>} obj - The object to be flattened.
     * @param {string} [parentKey=''] - The base key prefix for flattened keys. Defaults to an empty string.
     * @param {number} [depth=Infinity] - The maximum depth to flatten the object. Defaults to infinite depth.
     * @return {Record<string, any>} A new object with flattened key-value pairs derived from the input object.
     */
    public flattenObject(
        obj: Record<string, any>,
        parentKey = '',
        depth = Infinity,
    ): Record<string, any> {
        const result: Record<string, any> = {};

        function recurse(
            current: Record<string, any>,
            prefix: string,
            remainingDepth: number,
        ): void {
            for (const key in current) {
                if (!Object.prototype.hasOwnProperty.call(current, key)) {
                    continue;
                }

                const value = current[key];
                const newKey = prefix ? `${ prefix }.${ key }` : key;

                if (
                    remainingDepth > 0 &&
                    typeof value === 'object' &&
                    value !== null &&
                    !Array.isArray(value)
                ) {
                    recurse(value, newKey, remainingDepth - 1);
                } else {
                    result[newKey] = value;
                }
            }
        }

        recurse(obj, parentKey, depth);
        return result;
    }

    /**
     * Converts a flattened object with dot-separated keys into a nested object.
     *
     * @param {Record<string, any>} flat - The flattened object where keys are
     *        dot-separated strings representing the nested structure.
     * @return {Record<string, any>} A nested object reconstructed from the
     *         flattened input.
     */
    public unflattenObject(flat: Record<string, any>): Record<string, any> {
        const result: Record<string, any> = {};

        for (const flatKey in flat) {
            if (!Object.prototype.hasOwnProperty.call(flat, flatKey)) {
                continue;
            }

            const value = flat[flatKey];
            const keys = flatKey.split('.');
            let current = result;

            for (let i = 0; i < keys.length; i++) {
                const k = keys[i];

                if (i === keys.length - 1) {
                    current[k] = value;
                } else {
                    if (!current[k] || typeof current[k] !== 'object') {
                        current[k] = {};
                    }

                    current = current[k];
                }
            }
        }

        return result;
    }

    /**
     * Removes the value at the specified nested key path within the given object by setting it to null.
     *
     * @param {Record<string, any>} obj - The object from which the deep key's value will be unset.
     * @param {string} path - The dot-separated string representing the key path to be unset.
     * @return {void}
     */
    public unsetDeepKey(obj: Record<string, any>, path: string): void {
        const keys = path.split('.');
        const lastKey = keys.pop();
        const target = keys.reduce((acc, k) => acc?.[k], obj);
        if (target && lastKey) {
            target[lastKey] = null;
        }
    }

    /**
     * Removes properties with nullish or invalid values (null, undefined, or NaN) from an object.
     *
     * @param {T} obj - The object to be processed for removal of nullish or invalid values.
     * @return {Partial<T>} A new object with all nullish and invalid values removed.
     */
    public removeNullish<T>(obj: T): Partial<T> {
        return Object.fromEntries(
            Object.entries(obj as Record<string, unknown>)
                  .filter(([_, v]) =>
                      v !== null
                      && v !== undefined
                      && !Number.isNaN(v),
                  ),
        ) as Partial<T>;
    }

    public removeEmptyValues<T>(obj: T): Partial<T> {
        return Object.fromEntries(
            Object.entries(obj as Record<string, unknown>)
                  .filter(([_, v]) => v?.toString().trim() !== ''),
        ) as Partial<T>;
    }

    /**
     * Checks if the provided object contains no nullish or invalid (NaN) values.
     * Nullish values include null, undefined, and NaN.
     *
     * @param {T} obj - The object to be checked for nullish or invalid values.
     * @return {boolean} Returns true if the object has no nullish or invalid values, otherwise false.
     */
    public hasNoNullish<T>(obj: T): boolean {
        return Object.values(obj as Record<string, unknown>)
                     .every(v => v !== null
                         && v !== undefined
                         && !Number.isNaN(v),
                     );
    }

    /**
     * Determines whether the given object is empty.
     *
     * @param {unknown} obj - The object to be checked.
     * @return {boolean} Returns true if the object has no own enumerable properties, otherwise false.
     */
    public isEmpty(obj: unknown): boolean {
        return Object.keys(obj as Record<string, unknown>).length === 0;
    }

    /**
     * The `assignIfNullOrUndefined` function updates the target object with properties from the source object
     * if the corresponding properties in the target object are `null` or `undefined`.
     *
     * @param {unknown} target - The target object to be updated with properties from the source object.
     * @param {unknown} source - The source object containing properties to be assigned to the target object.
     * @returns {{ [k: string]: unknown }} A new object that is the result of merging the target object with the filtered source object.
     */
    public assignIfNullOrUndefined = (target: unknown, source: unknown): { [k: string]: unknown } =>
        Object.assign(target, Object.fromEntries(Object.entries(source).filter(([k, v]) => target[k] === null || target[k] === undefined)));

    /**
     * This function iterates over the entries of the given object, filters out the ones with null or empty string values,
     * and returns a new object that only includes the non-null fields.
     * The use of Partial<T> as the return type indicates that the function may return an object with a subset of the original object's properties.
     *
     * @param {T} obj The object to be filtered.
     * @return {Partial<T>} A new object containing only the fields with non-null and non-empty string values.
     */
    public filterObjectFields<T>(obj: T): Partial<T> {
        return Object.entries(obj).reduce((acc, [key, value]) => {
            if (value !== null && value !== '') {
                acc[key as keyof T] = value;
            }
            return acc;
        }, {} as Partial<T>);
    }

    /**
     * Retrieves the value at a specified path within an object. If the path does not exist, it returns the provided fallback value.
     *
     * @param {unknown} obj - The object to retrieve the value from.
     * @param {string} path - The path to the desired value within the object, specified as a dot-separated string or with bracket notation.
     * @param {any} [fallback] - The value to return if the specified path does not exist within the object.
     * @return {any} - The value found at the specified path or the fallback value if the path does not exist.
     */
    public getByPath(obj: unknown, path: string, fallback?: any): any {
        if (!path) {
            return fallback;
        }

        const segments = path
            .replace(/\[(\w+)]/g, '.$1') // convert [0] to .0
            .split('.');

        return segments.reduce((acc, key) => {
            return (acc && acc[key] !== undefined)
                ? acc[key]
                : undefined;
        }, obj) ?? fallback;
    }
}
